// #region imports

// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { of } from 'rxjs';
import { catchError, switchMap, withLatestFrom } from 'rxjs/operators';

// 3rd Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

// --- HS Code
import { ReportsBaseService } from './reports-base.service';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ReportListItem, ApplicationError } from 'src/app/models';
import { AppService } from '../app.service';

import moment from 'moment';
import { OidcService } from '../account/oidc.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class ReportsService extends ReportsBaseService {
  constructor(
    protected modalService: NgbModal,
    private http: HttpClient,
    private appService: AppService,
    private translate: TranslateService,
    private oidcService: OidcService,
  ) {
    super(modalService);
  }

  loadReports(): void {
    const url =
      environment.apiConfig.baseUrl + environment.apiConfig.reportsApi;

    this.http
      .get<ReportListItem[]>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe(reports => {
        this._reportsList$.next(reports);
      });
  }

  getReportOnline(identifier: string): void {
    const url =
      environment.apiConfig.baseUrl + environment.apiConfig.reportsRequestTokenApi + `/${identifier}`;

    this._reportIsLoading$.next(true);

    this.http
      .get(url, { responseType: 'text' })
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe(result => {
        this._reportIsLoading$.next(false);
        this._showReport(result);
      });
  }

  getReportPdf(identifier: string): void {
    const url =
      environment.apiConfig.baseUrl + environment.apiConfig.downloadApi + `/${identifier}`;

    this._reportIsDownloading$.next(true);


    this.http
      .get(url, { responseType: 'blob' })
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this)),
        withLatestFrom(this.oidcService.identity$),
      )
      .subscribe(([result, user]) => {
        const names = [];
        if (user && user.profile && user.profile.given_name) {
          names.push(user.profile.given_name);
        }
        if (user && user.profile && user.profile.family_name) {
          names.push(user.profile.family_name);
        }
        this.handleDownloadSuccess(`PersonalEdition_${names.join('_')}_${moment().format('YYYYMMDDHHmm')}.pdf`, result);
        this._reportIsDownloading$.next(false);
      });
  }

  private handleDownloadSuccess(fileName: string, response: Blob) {
      const iOS = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
      if (iOS) {
        const reader = new FileReader();
        reader.readAsDataURL(response);
        reader.onloadend = () => {
          const base64data = reader.result;
          this.generateDownloadLink(fileName, base64data as string);
        };
        return;
      }
      this.generateDownloadLink(fileName, window.URL.createObjectURL(response));
  }

  private generateDownloadLink(fileName: string, downloadUrl: string) {
    const downloadLink = document.createElement('a');
    downloadLink.href = downloadUrl;

    downloadLink.setAttribute('download', fileName);
    document.body.appendChild(downloadLink);
    downloadLink.click();

    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(downloadUrl);
  }

  private _modelError(err) {
    this._handleError(err);
    return of(null as ReportListItem[]);
  }

  private _handleError(err) {
    this.appService.setLoading(false);
    this._reportIsLoading$.next(false);

    let error: ApplicationError;
    if (err && err instanceof HttpErrorResponse) {
      // A client-side or network error occurred. Handle it accordingly.
      let detailedMessage = `${err.message}`;
      if (err.statusText) {
        detailedMessage += `<br/>${err.statusText}`;
      }
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        errorNumber: err.status,
        detailedMessage
      } as ApplicationError;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        originalError: err
      } as ApplicationError;
    }
    this.appService.setLastError(error);
  }
}
